import { Scene } from '../core/Scene';

export interface ExportOptions {
    width?: number;
    height?: number;
    format?: 'png' | 'jpeg';
    quality?: number; // 0-1, for jpeg
    filename?: string;
    download?: boolean; // auto-download or return dataURL
}

/**
 * ExportManager — Captures the current 3D scene as an image.
 * Works because WebGLRenderer has preserveDrawingBuffer: true.
 */
export class ExportManager {
    /**
     * Capture current frame as data URL
     */
    static capture(options: ExportOptions = {}): string {
        const renderer = Scene.Instance.getRenderer();
        const canvas = renderer.domElement;

        const { width, height, format = 'png' } = options;

        // If custom size requested, render at that size
        if (width && height) {
            const sizeTarget = { x: 0, y: 0 };
            try { renderer.getSize(sizeTarget as any); } catch {}
            const currentWidth = sizeTarget.x || window.innerWidth;
            const currentHeight = sizeTarget.y || window.innerHeight;
            const currentPixelRatio = renderer.getPixelRatio();

            renderer.setSize(width, height);
            renderer.setPixelRatio(1);

            // Force a render at the new size
            const scene = (Scene.Instance as any).scene;
            const camera = (Scene.Instance as any).camera;
            if (scene && camera) {
                camera.aspect = width / height;
                camera.updateProjectionMatrix();
                renderer.render(scene, camera);
            }

            const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
            const dataURL = canvas.toDataURL(mimeType, options.quality ?? 0.95);

            // Restore original size
            renderer.setSize(currentWidth, currentHeight);
            renderer.setPixelRatio(currentPixelRatio);
            if (camera) {
                camera.aspect = window.innerWidth / window.innerHeight;
                camera.updateProjectionMatrix();
            }

            return dataURL;
        }

        const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
        return canvas.toDataURL(mimeType, options.quality ?? 0.95);
    }

    /**
     * Download current frame as image file
     */
    static download(options: ExportOptions = {}): void {
        const dataURL = ExportManager.capture(options);
        const ext = options.format === 'jpeg' ? 'jpg' : 'png';
        const filename = options.filename || `3d-export-${Date.now()}.${ext}`;

        const link = document.createElement('a');
        link.href = dataURL;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    /**
     * Capture as Blob (for API usage or clipboard)
     */
    static async captureBlob(options: ExportOptions = {}): Promise<Blob> {
        const dataURL = ExportManager.capture(options);
        const response = await fetch(dataURL);
        return response.blob();
    }

    /**
     * Copy screenshot to clipboard
     */
    static async copyToClipboard(options: ExportOptions = {}): Promise<void> {
        const blob = await ExportManager.captureBlob({ ...options, format: 'png' });
        await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
        ]);
    }
}

/**
 * Parse export-related URL parameters
 */
export function getExportParams(): {
    isExportMode: boolean;
    width: number;
    height: number;
    format: 'png' | 'jpeg';
    autoDownload: boolean;
    poi: number;
    delay: number;
} {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    const size = params.get('size') || params.get('s') || '1920x1080';
    const [w, h] = size.split('x').map(Number);

    return {
        isExportMode: mode === 'export',
        width: w || 1920,
        height: h || 1080,
        format: (params.get('format') || params.get('f') || 'png') as 'png' | 'jpeg',
        autoDownload: params.get('download') !== 'false',
        poi: parseInt(params.get('poi') || '0', 10),
        delay: parseInt(params.get('delay') || '2000', 10),
    };
}
